import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PasswordResetForm } from "@/components/auth/PasswordResetForm";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const PasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [tokenFound, setTokenFound] = useState(false);

  useEffect(() => {
    // Check for tokens in multiple possible locations
    
    // 1. Check URL hash directly
    let hashValue = location.hash;
    
    // 2. Check if we have a recovery token in the location state (from our redirect)
    if (location.state?.recoveryToken) {
      hashValue = location.state.recoveryToken;
      console.log("Using recovery token from state:", hashValue);
    }
    
    // 3. Process hash to extract tokens if present
    if (hashValue && hashValue.includes('access_token=')) {
      console.log("Found token hash:", hashValue);
      try {
        // Extract the token parameters from the URL fragment
        const fragment = hashValue.substring(1); // Remove the leading #
        const params = new URLSearchParams(fragment);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');

        if (accessToken && refreshToken) {
          setTokenFound(true);
          console.log('Password reset tokens found');
          
          // Set the session with the tokens
          const setSession = async () => {
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (error) {
              console.error('Error setting session:', error);
              setError('Invalid or expired password reset link. Please request a new one.');
            } else {
              console.log('Session set successfully');
            }
          };
          
          setSession();
        } else {
          console.error('Access or refresh token missing');
          setError('Invalid password reset link. Please request a new one.');
        }
      } catch (err) {
        console.error('Error parsing recovery URL:', err);
        setError('Invalid password reset link format. Please request a new one.');
      }
    } else {
      // No tokens found, check for error parameters
      const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));
      const errorCode = hashParams.get('error_code');
      const errorDescription = hashParams.get('error_description');
      
      if (errorCode) {
        console.error('Auth error detected:', errorCode, errorDescription);
        const errorMessage = errorDescription || 'An authentication error occurred.';
        setError(errorMessage);
      } else if (!tokenFound) {
        // No tokens and no errors
        console.log('No recovery tokens found in URL');
        // We'll keep the user on this page, but they may need to request a new reset link
      }
    }

    // Clean the URL without reloading the page (but preserve state)
    if (hashValue) {
      window.history.replaceState(
        { ...window.history.state, recoveryToken: hashValue },
        document.title,
        location.pathname
      );
    }
  }, [location, navigate]);

  const handlePasswordReset = async (data: { password: string }) => {
    setIsResetting(true);
    setError(null);

    try {
      const { error } = await supabase.auth.updateUser({ 
        password: data.password
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Password Reset Successful",
        description: "Your password has been successfully reset. You can now log in with your new password.",
      });

      // Redirect to login page after successful password reset
      setTimeout(() => {
        navigate("/auth");
      }, 2000);

    } catch (error) {
      console.error('Password reset error:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to reset password.";
      setError(errorMessage);
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <img
              src="/diy-legal-logo.png"
              alt="DIYLegal Logo"
              className="h-24 w-auto mb-6"
              onError={(e) => {
                console.error('Error loading logo:', e);
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
          <CardDescription className="text-center">
            {tokenFound ? "Reset Your Password" : "Password Reset"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {tokenFound ? (
            <>
              <p className="text-center text-sm text-gray-600 mb-4">
                Please enter your new password below.
              </p>
              <PasswordResetForm 
                onSubmit={handlePasswordReset} 
                isLoading={isResetting} 
              />
            </>
          ) : (
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                {error 
                  ? "There was a problem with your reset link. Please return to the login page and request a new one."
                  : "Waiting for authentication token..."}
              </p>
              <Button onClick={() => navigate("/auth")}>
                Return to Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PasswordReset;
